/**
 * ExtraStatus virtual views (B2): nine de-prefixed, ancestry-enriched surfaces
 * fronting the per-string Agent sources that had no virtual view and were
 * therefore hidden from the agent catalog.
 *
 * Every source here carries a `wellString` producing-string relation, so each
 * is per-string grain and built as a StringContext enriched view — keeping
 * identity + the wellString relation (`STRING_KEEP`) and appending
 * well/field/reservoir ancestry (`STRING_CONTEXT_ENRICH`). None has a source
 * prefix: the source column names are already bare, so each group keeps the
 * default empty prefix (`suffix === canonical`).
 *
 * Built per session (not module-level) because the enriched build closes over
 * the CDF client + hierarchy/entity views to custom-fetch and resolve ancestry.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  GAS_INJECTION_PERFORMANCE,
  IDENTITY,
  INACTIVE_STATUS,
  INJ_PRESS_GUIDELINE,
  INJECTION_ALLOWABLE,
  INJECTION_DIAGNOSTICS,
  MOVEMENT_CONTROL_PARAMETER,
  MOVEMENT_RATES,
  MOVEMENT_RECORD,
  MOVEMENT_TIMING,
  PRODPLAN_ALLOWABLE,
  PRODUCER_GUIDELINES,
  PVT_SAMPLE,
  type Shape,
  stringRelation,
  WATER_INJECTION_PERFORMANCE,
  WELL_CLASSIFICATION,
  WELL_ONLINE_STATE,
  WELL_POTENTIAL,
  WELL_PRESSURE_COMPLIANCE,
  WELL_VALVE_STATES,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import {
  InactiveStringView,
  InjectionPerformanceStringAgentView,
  InjPressGuidelineStringAgentView,
  ProdplanStringAgentView,
  ProducerGuidelinesStringAgentView,
  PvtAnalysisStringAgentView,
  WellMovementAgentView,
  WellPotentialStringAgentView,
  WellStatusStringAgentView,
} from '#schema/generated/ipso/views.ts';

/**
 * One concern-scoped, per-string source surface: its agent-facing name, doc,
 * the source CDF view it reshapes, and the de-prefixed groups to keep. All
 * groups use bare names (empty prefix) — these sources expose no prefix axis.
 */
type ExtraStatusSpec = ViewFamilySpec<readonly { readonly shape: Shape }[]> & {
  readonly source: DataModelView;
};

/**
 * The nine per-string status surfaces, declared as data. Each de-prefixes
 * (here: passes through under bare canonical names) one or more concern
 * groups; all share identity + the producing-string relation (canonically
 * `wellString`) and the well/field/reservoir ancestry enrichment. Sources are
 * the upstream Agent views, except where a base solution-model view is richer
 * (InactiveString) — then a per-spec `keep` remaps the relation column.
 */
const EXTRA_STATUS_SPECS: readonly ExtraStatusSpec[] = [
  {
    name: 'PvtAnalysis',
    description: `Per-string PVT fluid-sample properties (salinity, sample date/type, water cut)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: PvtAnalysisStringAgentView,
    groups: [{ shape: PVT_SAMPLE }],
  },
  {
    name: 'InactiveString',
    description: `Per-string long-term inactivity and monthly classification: binary string status, most-specific status tier, root-cause category/sub-category/problem name, downtime start/duration, remediation action and expected restored rate${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    // Sourced from the base solution-model view — the upstream Agent view
    // exposes only status/category/downtime-start. The base view names the
    // producing-string relation `entityName`; keep it under the canonical
    // `wellString` so scoping and ancestry enrichment match the other
    // per-string surfaces.
    source: InactiveStringView,
    keep: [...IDENTITY, stringRelation('entityName')],
    groups: [{ shape: INACTIVE_STATUS }],
  },
  {
    name: 'InjectionPerformance',
    description: `Per-string injection performance: gas- and water-injection rates/targets/technical rates/pressures/cumulative volumes plus injection-efficiency and Hall-plot diagnostics${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: InjectionPerformanceStringAgentView,
    groups: [
      { shape: GAS_INJECTION_PERFORMANCE },
      { shape: WATER_INJECTION_PERFORMANCE },
      { shape: INJECTION_DIAGNOSTICS },
    ],
  },
  {
    name: 'WellPotential',
    description: `Per-string unconstrained potential: potential oil/gas/water production and water/gas injection rates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: WellPotentialStringAgentView,
    groups: [{ shape: WELL_POTENTIAL }],
  },
  {
    name: 'WellStatus',
    description: `Per-string operational status: online state/reason, steady-state and slugging indicators, well classification (lift/well type, product, WAG, instrumented), surface/sub-surface/wing valve states, and pressure compliance + reservoir-pressure estimates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: WellStatusStringAgentView,
    groups: [
      { shape: WELL_ONLINE_STATE },
      { shape: WELL_CLASSIFICATION },
      { shape: WELL_VALVE_STATES },
      { shape: WELL_PRESSURE_COMPLIANCE },
    ],
  },
  {
    name: 'InjPressGuideline',
    description: `Per-string injection pressure/rate guidelines: max gas/water-injection well-head (WHIP) and bottom-hole (BHIP) pressures and max injection rates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: InjPressGuidelineStringAgentView,
    groups: [{ shape: INJ_PRESS_GUIDELINE }],
  },
  {
    name: 'ProducerGuidelines',
    description: `Per-string producer operating guidelines — the only producer rate-limit source: max GOR, min BHP/WHP, max liquid/oil rate; no max-gas or max-water limit exists, never fabricate one${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: ProducerGuidelinesStringAgentView,
    groups: [{ shape: PRODUCER_GUIDELINES }],
  },
  {
    name: 'Prodplan',
    description: `Per-string production-plan allowable: quota contribution, total-gas WSOPR and technical oil rate, plus water/gas allowable rates/contributions and technical injection rates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: ProdplanStringAgentView,
    groups: [{ shape: PRODPLAN_ALLOWABLE }, { shape: INJECTION_ALLOWABLE }],
  },
  {
    name: 'WellMovement',
    description: `Per-string well-movement records: scenario/status metadata, created/planned/actual timing and do-not-disturb window, adjusted control parameter with pre/post-optimized values, and baseline vs estimated oil/gas/water rates${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    source: WellMovementAgentView,
    groups: [
      { shape: MOVEMENT_RECORD },
      { shape: MOVEMENT_TIMING },
      { shape: MOVEMENT_CONTROL_PARAMETER },
      { shape: MOVEMENT_RATES },
    ],
  },
];

/**
 * Build the nine de-prefixed, ancestry-enriched per-string surfaces, each a
 * StringContext view over its own source + the shared well/field/reservoir
 * enrichment; filter by wellString (or join WellEntity) to scope by hierarchy.
 */
export const useExtraStatusViews = makeStringContextViews({
  source: (spec: ExtraStatusSpec) => spec.source,
  specs: EXTRA_STATUS_SPECS,
});
